import { Dispatch } from "react-redux";
import { AuthActionCreators, IApplicationState } from "..";
import { IBaseResponse, IDonorRegistration, IDashRegistrationLog, IDashRegistrationRequest, IDonorDiagnostic, IDonorDiagnosticRequest, ICenterBase, IListItem, IDonorStatus, IWorkflowStatus, IRemoteDonationPreferred, IDonorConnectEmailRequest, IDonorSearchRequest, IDonorDetailsRequest, IContact, IDonorStatusByCenter, ICoordinatorListByCenter } from "../../models/index";
import { IDashWorkFlowStatusRequest } from "../../models/dash-workflow-status-request";
import { IPost } from "../authorization";
import { IDelete } from '../authorization/actions';
import { IDashRemoteStatus } from "@nkr/models/donor-status-by-center";

export interface ICenterDashListResponse {
    data: {
        success: boolean,
        results: IDonorRegistration[]
    }
}

export interface ICenterDashDetailResponse {
    data: {
        success: boolean,
        results: IDonorRegistration
    }
}

export interface IDashLogListResponse {
    data: {
        success: boolean,
        message: string,
        results: IDashRegistrationLog[]
    }
}

export interface IWorkflowStatusResponse {
    data: {
        success: boolean,
        results: IWorkflowStatus
    }
}

export interface IDashLookupDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            statusList: IDonorStatus[],
            actionList: IListItem[],
            userList: { id: number, firstName: string, lastName: string, donorConnectCaseLoad: number }[]
        }
    }
}

export interface IDashLogResponse {
    data: {
        success: boolean,
        message: string,
        results: IDashRegistrationLog
    }
}

export interface IRecipientCoordListLookupDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            recipientCenterCoordinatorList: IContact[]
        }
    }
}

export interface IDashCenterLookupDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            emailTemplate: string,
            statusListByCenter: IDonorStatusByCenter[],
            remoteStatusList: IDashRemoteStatus[],
            stateList: IListItem[],
            actionList: IListItem[],
            donationTypeList: IListItem[],
            dispositionList: IListItem[],
            coordinatorListByCenter: ICoordinatorListByCenter[],
            centerList: ICenterBase[],
            ruleOutReasons: IListItem[]
        }
    }
}

export interface IStatusListResponse {
    data: {
        success: boolean,
        message: string,
        results: IWorkflowStatus[]
    }
}

export interface IDispositionListResponse {
    data: {
        success: boolean,
        results: IListItem[]
    }
}

export interface IReportRemoteDonationPreferredResponse {
    data: {
        success: boolean,
        results: IRemoteDonationPreferred[]
    }
}

export interface IDiagnosticResponse {
    data: {
        success: boolean,
        message: string,
        results: IDonorDiagnostic
    }
}

export interface IDonorIntakeFullNameResponse {
    data: {
        success: boolean,
        message: string,
        results: string
    }
}

export interface IDiagnosticLookupDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            registrationStepList: IListItem[],
            donationTypeList: IListItem[],
            stateList: IListItem[],
            donorTypeList: IListItem[],
            remissionStatusList: IListItem[],
            diabetesStatusList: IListItem[],
            yesNoUnknownList: IListItem[],
            preWorkUpLabList: IListItem[],
            connectToDonorGroupList: IListItem[],
            centerList: ICenterBase[],
            recipientCenters: IStateCenterOption[],
            countryList: IListItem[]
        }
    }
}

export interface IUploadLabsResponse {
    data: {
        success: boolean,
        results: IDonorRegistration
    }
}

export interface IStateCenterOption {
    id: number,
    alias: string,
    name: string,
    state: string
}

export interface IRegistrationDetailsForPwl {
    centerId: number,
    registrationDateTime: string,
    historyCompleteDateTime: string,
    firstName: string,
    lastName: string,
    email: string,
    phoneNumber: string,
    birthDate: string,
    age: number,
    addressLine1: string,
    city: string,
    stateId: number,
    state: string,
    postalCode: string,
    heightSubFeet: number,
    heightSubInches: number,
    weightLbs: number,
    hasIntendedRecipient: boolean,
    donorSituationId: number,
    donorSituation: string,
    recipientLocationId: number,
    recipientLocation: string,
    recipientCenter: string,
    ddRecipientFirstName: string,
    ddRecipientLastName: string,
    ddKnowsRecipientDob: boolean,
    ddRecipientDob: string,
    ddRecipientUrgencyId: number,
    ddRecipientUrgency: string,
    ddRelationshipToPatientId: number,
    ddRelationshipToPatient: string,
    ddFrequencyOfCommunicationId: number,
    ddFrequencyOfCommunication: string,
    isClosed: boolean
}

export interface IPwlDetails {
    jugStatusId: number,
    jugStatus: string,
    jugReceivedDateTime: string,
    scripUploadDateTime: string,
    scripExpirationDate: string,
    questCompletedDate: string,
    questCompletedWhere: string,
    labOrderRequisitionNumber: string,
    preWorkupLabStepId: number
    preWorkupLabStep: string,
    scripUploadFile: string,
    labsUploadFile: string,
    labsUploadFileJson: string,
    labResultsAreReviewed: boolean
}

export interface IShippedJug {
    id: number,
    requestedDateTime: string,
    shippedDateTime: string,
    arrivalDateTime: string,
    notFoundDateTime: string,
    statusId: number,
    status: string
}

export interface IResultsUploaded {
    id: number,
    requisitionNumber: string,
    uploadDateTime: string,
    isBillable: boolean
}

export interface IPwlDetailsResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            registrationDetails: IRegistrationDetailsForPwl,
            pwlDetails: IPwlDetails,
            shippedJugsList: IShippedJug[],
            resultsUploadedList: IResultsUploaded[]
        }
    }
}

export interface ICheckDonorProfileResponse {
    data: {
        success: boolean,
        results: {
            exists: boolean,
            alias: string
        }
    }
}

export interface IExpertSystemEnrollResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            donorId: number,
            existingTyping: boolean,
            existingRequestForTyping: boolean,
            existingSwabMessage?: string
        }
    }
}

export interface ICreateDonorForWatrResponse {
    data: {
        success: boolean,
        message?: string,
        results: {
            donorId: number,
            watrCenterId: number,
            hasExistingWatrRequest: boolean,
            existingWatrId: number,
            wasAlreadyLinked: boolean,
            watrRequestId: number,
            watrEditKey: string,
            donorShieldDashboardUrl: string
        }
    }
}

export const ActionCreators = {
    getDonorRegistrationSearchIntendedList: (data: IDonorSearchRequest, isSuperAdmin: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterDashListResponse> => {
        let postUrl = isSuperAdmin && data.centerId == 0 ?
            `${__API__}/admin/dash/donor-intake/search`
            :
            `${__API__}/center/${data.centerId}/dash/search`;

        let options: IPost = {
            url: postUrl,
            data: {
                centerId: data.centerId,
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                includeNkr: data.includeNkr,
                includeDash: data.includeDash,
                includeMiSite: data.includeMiSite,
                filterStartDate: data.filterStartDate,
                filterEndDate: data.filterEndDate,
                disposition: data.disposition,
                dateSearchType: data.dateSearchType,
                dateOfBirth: data.dateOfBirth,
                showFilter: data.showFilter,
                centerStatus: data.centerStatus,
                isInEmr: data.isInEmr,
                coordinatorId: data.coordinatorId,
                organType: data.organType
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getDonorRegistrationAllList: (data: IDonorSearchRequest, isSuperAdmin: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterDashListResponse> => {
        let postUrl = isSuperAdmin && data.centerId == 0 ?
            `${__API__}/admin/dash/donor-intake/search`
            :
            `${__API__}/center/${data.centerId}/dash/search`;

        let options: IPost = {
            url: postUrl,
            data
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getDonorDetails: (centerId: number, regId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterDashDetailResponse> => {
        var options = {
            url: `${__API__}/center/${centerId}/dash/details/${regId}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    donorDetailsPrint: (centerId: number, regId: number) => (dispatch: Dispatch<IApplicationState>) => {
        let options = {
            url: __API__ + `/center/${centerId}/dash/print/${regId}`,
            responseType: 'blob'
        }

        return dispatch(AuthActionCreators.apiGet(options));
    },
    handleLoginAs: (regId: number) => (dispatch: Dispatch<IApplicationState>) => {
        let options = {
            url: __API__ + `/center/dash/${regId}/login-as`
        }

        return dispatch(AuthActionCreators.apiGet(options));
    },

    getDonorRegistrationAllListExport: (data: IDonorSearchRequest) => (dispatch: Dispatch<IApplicationState>): Promise<ICenterDashListResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${data.centerId}/dash/all?csv=true`,
            data: {
                CenterId: data.centerId,
                FirstName: data.firstName,
                LastName: data.lastName,
                Email: data.email,
                IncludeNkr: data.includeNkr,
                IncludeDash: data.includeDash,
                IncludeMiSite: data.includeMiSite,
                FilterStartDate: data.filterStartDate,
                FilterEndDate: data.filterEndDate,
                Disposition: data.disposition,
                DateSearchType: data.dateSearchType,
                DateOfBirth: data.dateOfBirth,
                ShowFilter: data.showFilter,
                CenterStatus: data.centerStatus,
                IsInEmr: data.isInEmr,
                organType: data.organType,
                followUpDays: data.followUpDays
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: ICenterDashListResponse) => {
            return Promise.resolve(response);
        });
    },

    getRecipientCenterCoordList: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientCoordListLookupDataResponse> => {
        var options = {
            url: `${__API__}/center/${centerId}/dash/recipient-center-lookup-data`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    updateRecipientCenterCoord: (centerId: number, donorId: number, coordinatorId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/${donorId}/update-recipient-coordinator/${coordinatorId}`
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },

    updateDetails: (centerId: number, regId: number, statusId: number, centerNextFollowUpDate: string, remoteStatus: number, onHoldStatusReason: string, remoteStatusDate: string, reviewComments: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let request: IDashWorkFlowStatusRequest = {
            centerId,
            registrationId: regId,
            statusId,
            centerNextFollowUpDate,
            reviewComments,
            remoteStatus,
            onHoldStatusReason,
            remoteStatusDate,
            ruleOutReasonId: null,
            allowReRegistration: null,
            useNkrAssistance: null,
            coordinatorName: null,
            phoneNumber: null,
            medicalReasonCategory: null,
            medicalReasonNotes: null
        };

        let options: IPost = {
            url: `${__API__}/center/dash/change-status`,
            data: request
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },



    changeStatus: (request: IDashWorkFlowStatusRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/dash/change-status`,
            data: request
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },

    changeAssignedCoordinator: (centerId: number, regId: number, contactId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/${regId}/assigned-coordinator/${contactId}`
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },

    unassignCoordinator: (centerId: number, regId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/${regId}/unassign-coordinator`
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },

    saveLogEntry: (centerId: number, dashRegistrationId: number, actionTakenId: number, comments: string) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterDashDetailResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/${dashRegistrationId}/log-entry`,
            data: {
                dashRegistrationId,
                actionTakenId,
                comments
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },

    updateLogEntry: (id: number, centerId: number, dashRegistrationId: number, actionTakenId: number, comments: string) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterDashDetailResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/${dashRegistrationId}/log-entry/${id}`,
            data: {
                id,
                actionTakenId,
                comments
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPut(options));
        } catch (error) {
            throw error;
        }
    },

    deleteLogEntry: (centerId: number, regId: number, id: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterDashDetailResponse> => {
        let options: IDelete = {
            url: `${__API__}/center/${centerId}/dash/${regId}/log-entry/${id}`
        };

        try {
            return await dispatch(AuthActionCreators.apiDelete(options));
        } catch (error) {
            throw error;
        }
    },

    getDashCenterLogEntries: (centerId: number, regId: number, isSuperAdmin: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IDashLogListResponse> => {
        let postUrl = isSuperAdmin && centerId == 0
            ? `${__API__}/admin/dash/donor-intake/${regId}/log-entries`
            : `${__API__}/center/${centerId}/dash/${regId}/log-entries`;

        let options = {
            url: postUrl
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    sendConfirmEmail: (data: IDonorConnectEmailRequest) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${data.centerId}/dash/${data.regId}/send-confirm-email`,
            data: {
                CenterId: data.centerId,
                Content: data.content,
                EmailSent: data.emailSent,
                RegId: data.regId,
                ReplyToEmail: data.replyToEmail,
                Subject: data.subject,
                ToEmail: data.toEmail
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    sendPediatricGalleryEmail: (centerId: number, regId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/${regId}/email/pediatric-gallery`,
            data: {}
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    markJugAsArrived: (donorId: number, arrivalDate: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/admin/donor/${donorId}/jug-arrived`,
            data: {
                arrivalDate
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    updateDonorDetails: (centerId: number, detailRequest: IDonorDetailsRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/donor-details`,
            data: {
                id: detailRequest.id,
                disposition: detailRequest.disposition,
                dispositionId: detailRequest.dispositionId,
                comments: detailRequest.comments,
                firstName: detailRequest.firstName,
                lastName: detailRequest.lastName,
                birthDate: detailRequest.birthDate,
                phoneNumber: detailRequest.phoneNumber,
                email: detailRequest.email,
                addressLine1: detailRequest.addressLine1,
                donorCity: detailRequest.donorCity,
                donorStateId: detailRequest.donorStateId,
                donorPostalCode: detailRequest.donorPostalCode
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPut(options));
        } catch (error) {
            throw error;
        }
    },

    updateDisposition: (centerId: number, detailRequest: IDonorDetailsRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/donor-disposition`,
            data: {
                id: detailRequest.id,
                disposition: detailRequest.disposition,
                dispositionId: detailRequest.dispositionId,
                closeLoopSurgeryDateTime: detailRequest.closeLoopSurgeryDateTime,
                closeLoopEntityTypeId: detailRequest.closeLoopEntityTypeId,
                closeLoopEntityType: detailRequest.closeLoopEntityType
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPut(options));
        } catch (error) {
            throw error;
        }
    },

    updateComments: (centerId: number, detailRequest: IDonorDetailsRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/donor-comments`,
            data: {
                id: detailRequest.id,
                comments: detailRequest.comments
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPut(options));
        } catch (error) {
            throw error;
        }
    },

    getDashCenterLookupData: (centerId: number, isSuperAdmin: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IDashCenterLookupDataResponse> => {
        let postUrl = isSuperAdmin && centerId == 0 ?
            `${__API__}/admin/dash/donor-intake/lookup-data`
            :
            `${__API__}/center/${centerId}/dash/lookup-data`;

        let options = {
            url: postUrl
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    getWorkflowStatusList: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IStatusListResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/dash/workflow-status`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    getDispositionList: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IDispositionListResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/dash/disposition-list`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    updateNextFollowUpDate: (centerId: number, registrationId: number, date: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/${registrationId}/next-followup-date?date=${date}`
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },
    addWorkflowStatus: (centerId: number, name: string, disposition: number, followUp: boolean, isInEmr: boolean, isRuledOut: boolean, isILDA: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IWorkflowStatusResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/workflow-status`,
            data: {
                description: name,
                associatedDisposition: disposition,
                addFollowUp: followUp,
                isInEmr: isInEmr,
                isRuledOut: isRuledOut,
                isILDA: isILDA
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },
    updateWorkflowStatus: (centerId: number, wfStatus: IWorkflowStatus) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/workflow-status/${wfStatus.id}`,
            data: {
                description: wfStatus.description,
                associatedDisposition: wfStatus.associatedDisposition,
                sortOrder: wfStatus.sortOrder,
                isInEmr: wfStatus.isInEmr,
                isRuledOut: wfStatus.isRuledOut,
                isILDA: wfStatus.isILDA,
                addFollowUp: wfStatus.addFollowUp
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPut(options));
        } catch (error) {
            throw error;
        }
    },
    deleteWorkflowStatus: (centerId: number, wfStatusId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IDelete = {
            url: `${__API__}/center/${centerId}/dash/workflow-status/${wfStatusId}`
        };

        return await dispatch(AuthActionCreators.apiDelete(options));
    },

    updateDonorRegistrationWorkflowStatus: (centerId: number, regId: number, statusId: number, centerNextFollowUpDate: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/${regId}/registration-status/workflow`,
            data: {
                statusId,
                centerNextFollowUpDate: centerNextFollowUpDate || null
            }
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },

    updateDonorRegistrationNetworkStatus: (
        centerId: number,
        regId: number,
        remoteStatus: number | null,
        remoteStatusDate: string | null,
        onHoldStatusReason: string | null,
        extras?: {
            applyForDonorShieldAdvancedReimbursement?: boolean | null,
            donorAlias?: string | null,
            advancedReimbursementDonorType?: number | null,
            ddRecipFirstName?: string | null,
            ddRecipLastName?: string | null,
            ddRecipDob?: string | null,
            recipientAlias?: string | null,
            linkedDonorShieldDirectId?: number | null,
            linkedDonorId?: number | null
        } | null
    ) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let data: Record<string, unknown> = {
            remoteStatus,
            remoteStatusDate: remoteStatusDate || null,
            onHoldStatusReason: onHoldStatusReason || null
        };
        if (extras) {
            const x = extras;
            if (x.applyForDonorShieldAdvancedReimbursement !== undefined && x.applyForDonorShieldAdvancedReimbursement !== null) {
                data.applyForDonorShieldAdvancedReimbursement = x.applyForDonorShieldAdvancedReimbursement;
            }
            if (x.donorAlias != null && x.donorAlias !== '') {
                data.donorAlias = x.donorAlias;
            }
            if (x.advancedReimbursementDonorType != null) {
                data.advancedReimbursementDonorType = x.advancedReimbursementDonorType;
            }
            if (x.ddRecipFirstName != null && x.ddRecipFirstName !== '') {
                data.ddRecipFirstName = x.ddRecipFirstName;
            }
            if (x.ddRecipLastName != null && x.ddRecipLastName !== '') {
                data.ddRecipLastName = x.ddRecipLastName;
            }
            if (x.ddRecipDob != null && x.ddRecipDob !== '') {
                data.ddRecipDob = x.ddRecipDob;
            }
            if (x.recipientAlias != null && x.recipientAlias !== '') {
                data.recipientAlias = x.recipientAlias;
            }
            if (x.linkedDonorShieldDirectId != null && x.linkedDonorShieldDirectId > 0) {
                data.linkedDonorShieldDirectId = x.linkedDonorShieldDirectId;
            }
            if (x.linkedDonorId != null && x.linkedDonorId > 0) {
                data.linkedDonorId = x.linkedDonorId;
            }
        }
        let options: IPost = {
            url: `${__API__}/center/${centerId}/dash/${regId}/registration-status/network`,
            data
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },

    getRemoteDonationPreferred: () => async (dispatch: Dispatch<IApplicationState>): Promise<IReportRemoteDonationPreferredResponse> => {
        let options = {
            url: `${__API__}/donor-intake/report/remote-donation-preferred`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getDiagnostic: (centerId: number, id: number, isSuperAdmin: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IDiagnosticResponse> => {
        var options = {
            url: isSuperAdmin
                ? `${__API__}/admin/dash/diagnostics/?registrationId=${id}`
                : `${__API__}/donor-intake/${centerId}/diagnostics/?registrationId=${id}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    getLookupData: () => async (dispatch: Dispatch<IApplicationState>): Promise<IDiagnosticLookupDataResponse> => {
        let options = {
            url: `${__API__}/donor-intake/diagnostics/lookup-data`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    saveDiagnostic: (request: IDonorDiagnosticRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IDiagnosticResponse> => {
        let options = {
            url: `${__API__}/donor-intake/update-diagnostics`,
            data: request
        };

        try {
            return await dispatch(AuthActionCreators.apiPut(options));
        } catch (error_1) {
            throw error_1;
        }
    },

    optInToMentor: (registrationId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/admin/dash/mentor-opt-in/${registrationId}`,
            data: {}
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error) {
            throw error;
        }
    },

    sentOptOutEmail: (request: IDonorDiagnosticRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IDiagnosticResponse> => {
        let options = {
            url: `${__API__}/donor-intake/send-opt-out-email`,
            data: request
        };

        try {
            return await dispatch(AuthActionCreators.apiPost(options));
        } catch (error_1) {
            throw error_1;
        }
    },

    enrollExpertSystem: (registrationId: number, bulkType: number, alias: string, medicalRecordId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemEnrollResponse> => {
        let url = bulkType == 1 ?
            `${__API__}/expert-system/place-donor-order-from-dash-bulk/${registrationId}?alias=${alias}&medicalRecordId=${medicalRecordId}`
            :
            bulkType == 0 ?
                `${__API__}/expert-system/place-donor-order-from-dash/${registrationId}?alias=${alias}&medicalRecordId=${medicalRecordId}`
                :
                `${__API__}/expert-system/create-expert-system-enrollment-from-dash/${registrationId}`;

        let options = {
            url
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },
    checkDonorProfile: (registrationId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICheckDonorProfileResponse> => {
        let options = {
            url: `${__API__}/expert-system/check-donor-profile/${registrationId}`
        };

        try {
            return await dispatch(AuthActionCreators.apiGet(options));
        } catch (error) {
            throw error;
        }
    },

    validateExpertSystemEnrollment: (registrationId: number, alias: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/expert-system/confirm-donor-from-dash/`,
            data: {
                registrationId,
                alias
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getPwlDetails: (registrationId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IPwlDetailsResponse> => {
        let options = {
            url: `${__API__}/donor-intake/${registrationId}/pwl-details`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getLabOrder: (registrationId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let options = {
            url: `${__API__}/donor-intake/${registrationId}/lab-order`,
            responseType: 'blob'
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getLabResultsCombined: (registrationId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let options = {
            url: `${__API__}/donor-intake/${registrationId}/lab-results-combined`,
            responseType: 'blob'
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getLabResultsNotCombined: (registrationId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let options = {
            url: `${__API__}/donor-intake/${registrationId}/lab-results-not-combined`,
            responseType: 'blob'
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getFullName: (registrationId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IDonorIntakeFullNameResponse> => {
        let options = {
            url: `${__API__}/donor-intake/${registrationId}/full-name`,
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    moveToBatch: (registrationId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/donor-intake/${registrationId}/move-to-batch`,
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    uploadScrip: (registrationId: number, file: File, expirationDate: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let formData = new FormData();
        formData.append('file', file);
        formData.append('expirationDate', expirationDate);

        let options = {
            url: `${__API__}/donor-intake/${registrationId}/upload-scrip`,
            data: formData
        };

        return await dispatch(AuthActionCreators.apiPostFormData(options));
    },

    uploadLabs: (registrationId: number, file: File) => async (dispatch: Dispatch<IApplicationState>): Promise<IUploadLabsResponse> => {
        let formData = new FormData();
        formData.append('file', file);

        let options = {
            url: `${__API__}/donor-intake/${registrationId}/upload-labs`,
            data: formData
        };

        return await dispatch(AuthActionCreators.apiPostFormData(options));
    },

    convertToDirectToCenter: (registrationId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/donor-intake/${registrationId}/convert-to-direct-to-center?centerId=${centerId}`,
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    markResultsReviewed: (registrationId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/donor-intake/${registrationId}/mark-results-reviewed?centerId=${centerId}`,
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    restartPwl: (registrationId: number, isCystatinC: boolean) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let url = isCystatinC ? `${__API__}/donor-intake/${registrationId}/restart-pwl-cystatin-c` : `${__API__}/donor-intake/${registrationId}/restart-pwl`;

        let options = { url };

        if (isCystatinC) {
            return await dispatch(AuthActionCreators.apiGet(options));
        }
        else {
            return await dispatch(AuthActionCreators.apiPost(options));
        }
    },

    ruleOutPwl: (registrationId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterDashDetailResponse> => {
        let options = {
            url: `${__API__}/donor-intake/${registrationId}/rule-out?centerId=${centerId}`,
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    expireDonor: (registrationId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterDashDetailResponse> => {
        let options = {
            url: `${__API__}/donor-intake/${registrationId}/expire-donor?centerId=${centerId}`,
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    ruleOut: (registrationId: number, isExpireRuleOut: boolean, isInEmr: boolean, disposition: number, ruleOutReasons: string) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterDashDetailResponse> => {
        let options = {
            url: `${__API__}/donor-intake/rule-out`,
            data: {
                registrationId,
                isExpireRuleOut,
                isInEmr,
                disposition,
                ruleOutReasons
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    reverseRuleOut: (registrationId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterDashDetailResponse> => {
        let options = {
            url: `${__API__}/donor-intake/reverse-rule-out`,
            data: {
                registrationId
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    optOutDonor: (registrationId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterDashDetailResponse> => {
        let options = {
            url: `${__API__}/donor-intake/${registrationId}/opt-out-donor?centerId=${centerId}`,
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    createDonorForWatr: (centerId: number, registrationId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICreateDonorForWatrResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/donor-intake/${registrationId}/create-donor-watr`
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    }
};
